import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Grid,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { ParteProduccionService } from '../../features/produccion/partes-produccion/services/ParteProduccionService';
import type { ParteProduccion } from '../../features/produccion/partes-produccion/types';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [partesAprobados, setPartesAprobados] = useState<ParteProduccion[]>([]);
  const [partesPendientes, setPartesPendientes] = useState<ParteProduccion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPartes();
  }, []);

  const loadPartes = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar todos los partes (usamos un pageSize grande para obtener todos)
      const response = await ParteProduccionService.getAll(1, 100);

      // Filtrar por estado
      const aprobados = response.data
        .filter(parte => parte.estado === 'APR')
        .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());

      const pendientes = response.data
        .filter(parte => parte.estado === 'PEN')
        .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());

      setPartesAprobados(aprobados);
      setPartesPendientes(pendientes);
    } catch (err) {
      console.error('Error cargando partes de producción:', err);
      setError('Error al cargar los partes de producción');
    } finally {
      setLoading(false);
    }
  };

  const handleParteClick = (parteId: number) => {
    navigate(`/produccion/partes-produccion/${parteId}?readonly=true`);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-PE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const renderPartesList = (partes: ParteProduccion[], tipo: 'aprobados' | 'pendientes') => {
    if (partes.length === 0) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 4 }}
        >
          No hay partes {tipo}
        </Typography>
      );
    }

    return (
      <List sx={{ width: '100%', p: 0 }}>
        {partes.map((parte, index) => (
          <React.Fragment key={parte.id}>
            {index > 0 && <Divider />}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleParteClick(parte.id)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  py: 2,
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Parte N° {parte.numeroComprobante}
                      </Typography>
                      <Chip
                        label={formatDate(parte.fecha)}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Cliente: {parte.cliente || 'N/A'}
                      </Typography>
                      {parte.numeroDerc && (
                        <Typography variant="body2" color="text.secondary">
                          DERC: {parte.numeroDerc}
                        </Typography>
                      )}
                      {parte.asociacion && (
                        <Typography variant="body2" color="text.secondary">
                          {parte.asociacion}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <VisibilityIcon sx={{ color: 'action.active', ml: 1 }} />
              </ListItemButton>
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.default',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 2, md: 3 },
        backgroundColor: 'background.default',
        minHeight: '100vh',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          mb: 3,
          fontWeight: 600,
          color: 'text.primary',
          fontSize: { xs: '1.5rem', md: '2rem' }
        }}
      >
        Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Partes Aprobados - Izquierda */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            sx={{
              p: 3,
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
              border: '1px solid #f1f5f9',
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircleIcon sx={{ color: 'success.main' }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                Partes Aprobados
              </Typography>
              <Chip
                label={partesAprobados.length}
                size="small"
                color="success"
                sx={{ ml: 'auto' }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
              {renderPartesList(partesAprobados, 'aprobados')}
            </Box>
          </Paper>
        </Grid>

        {/* Partes Pendientes - Derecha */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            sx={{
              p: 3,
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
              border: '1px solid #f1f5f9',
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HourglassEmptyIcon sx={{ color: 'warning.main' }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                Partes Pendientes
              </Typography>
              <Chip
                label={partesPendientes.length}
                size="small"
                color="warning"
                sx={{ ml: 'auto' }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
              {renderPartesList(partesPendientes, 'pendientes')}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;